/**
 * Subscribe to the most recent drawing job for the given (conversationId, messageId).
 *
 * Uses Firestore's onSnapshot on a query so the UI updates live as the job
 * progresses through queued → running → succeeded.
 *
 * Requires a Firestore composite index on the `drawingJobs` collection group:
 *   - messageId (asc) + createdAt (desc).
 * See firestore.indexes.json.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { firestore } from '@/lib/firebase';
import type { DrawingJob } from '@shared/types';

export type UseLatestDrawingJobState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'none' } // no jobs yet
  | { status: 'ready'; job: DrawingJob }
  | { status: 'error'; error: Error };

export function useLatestDrawingJob(
  conversationId: string | null | undefined,
  messageId: string | null | undefined,
): UseLatestDrawingJobState {
  const [state, setState] = useState<UseLatestDrawingJobState>({
    status: 'idle',
  });

  useEffect(() => {
    if (!conversationId || !messageId) {
      setState({ status: 'idle' });
      return;
    }
    setState({ status: 'loading' });
    const q = query(
      collection(firestore, 'conversations', conversationId, 'drawingJobs'),
      where('messageId', '==', messageId),
      orderBy('createdAt', 'desc'),
      limit(1),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) {
          setState({ status: 'none' });
          return;
        }
        setState({
          status: 'ready',
          job: snap.docs[0].data() as DrawingJob,
        });
      },
      (err) => setState({ status: 'error', error: err as Error }),
    );
    return () => unsub();
  }, [conversationId, messageId]);

  return state;
}
